import React, { useState, useCallback, useRef, useEffect } from 'react'

import { TextField } from '@nike/eds'
import { Search } from '@nike/nike-design-system-icons'

type GlobalFilterProps = {
  globalFilter: string
  handleGlobalFilterChange: (value: string) => void
  placeholder?: string
  liveSearch?: boolean
}

export function GlobalFilter({ globalFilter, handleGlobalFilterChange, placeholder, liveSearch }: GlobalFilterProps) {
  const [inputValue, setInputValue] = useState(globalFilter || '');
  const debounceRef = useRef<NodeJS.Timeout>();
  const lastSearchValueRef = useRef<string>('');

  const debouncedSearch = useCallback((value: string) => {
    // Clear any existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Don't search if value hasn't actually changed
    if (value === lastSearchValueRef.current) {
      return;
    }
    
    debounceRef.current = setTimeout(() => {
      lastSearchValueRef.current = value;
      handleGlobalFilterChange(value);
    }, 500); 
  }, [handleGlobalFilterChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (liveSearch) {
      debouncedSearch(value);
    } else if (value === '') {
      // Clear immediately when input is cleared
      lastSearchValueRef.current = '';
      handleGlobalFilterChange('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!liveSearch && e.key === 'Enter') {
      // Clear any pending debounced search
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      lastSearchValueRef.current = inputValue;
      handleGlobalFilterChange(inputValue);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (globalFilter !== inputValue) {
      setInputValue(globalFilter || '');
      lastSearchValueRef.current = globalFilter || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter]);

  return (
    <TextField
      id='table--global-filter'
      className='base-table-global-search'
      type='search'
      label=''
      placeholder={placeholder || 'Search...'}
      beforeSlot={<Search />}
      value={inputValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      hideLabel
    />
  )
}
